import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { AlertCircle, Check, Clock3, Crown, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';

const PERIODS = [
  { id: 'monthly', label: 'Mensal', amount: 9.9, cadence: 'por mês', equivalent: 'R$ 9,90/mês' },
  { id: 'quarterly', label: 'Trimestral', amount: 27.9, cadence: 'a cada 3 meses', equivalent: 'R$ 9,30/mês' },
  { id: 'semiannual', label: 'Semestral', amount: 51.9, cadence: 'a cada 6 meses', equivalent: 'R$ 8,65/mês' },
  { id: 'annual', label: 'Anual', amount: 95.9, cadence: 'por ano', equivalent: 'R$ 7,99/mês' },
];

const RESOURCE_LABELS = {
  users: 'Acessos',
  customers: 'Clientes',
  ingredients: 'Ingredientes',
  recipes: 'Receitas',
  orders: 'Pedidos neste mês',
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatDate = (value) => {
  if (!value) return '';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long' }).format(new Date(value));
};

const Billing = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [acceptRenewal, setAcceptRenewal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);

  const loadSummary = useCallback(async (refresh = false) => {
    try {
      const response = await axios.get(`${API}/billing/me`);
      setSummary(response.data);
      if (refresh) {
        const updated = await axios.post(`${API}/billing/refresh`);
        setSummary(updated.data);
        return updated.data;
      }
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível carregar o plano');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    const returnedFromCheckout = searchParams.has('checkout') || searchParams.has('preapproval_id');

    const initialize = async () => {
      setLoading(true);
      if (!returnedFromCheckout) {
        if (active) await loadSummary();
        return;
      }

      for (let attempt = 0; attempt < 5 && active; attempt += 1) {
        const current = await loadSummary(attempt > 0);
        if (current && current.billing_status !== 'pending' && current.plan !== 'Free') break;
        await new Promise((resolve) => setTimeout(resolve, 1800));
      }
      if (active) setSearchParams({}, { replace: true });
    };

    initialize();
    return () => { active = false; };
  }, [loadSummary, searchParams, setSearchParams]);

  const selectedOption = useMemo(
    () => PERIODS.find((period) => period.id === selectedPeriod) || PERIODS[0],
    [selectedPeriod]
  );

  const startCheckout = async (existingUrl = null) => {
    if (existingUrl) {
      window.location.assign(existingUrl);
      return;
    }
    if (!acceptRenewal) {
      toast.error('Confirme a cobrança recorrente para continuar.');
      return;
    }
    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API}/billing/checkout`, {
        billing_cycle: selectedPeriod,
        accept_auto_renewal: true,
      });
      window.location.assign(response.data.checkout_url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível iniciar o checkout');
      await loadSummary();
    } finally {
      setCheckoutLoading(false);
    }
  };

  const cancelSubscription = async () => {
    const trialActive = summary?.plan === 'Trial';
    const message = trialActive
      ? 'Cancelar a renovação automática? O trial continua até a data final exibida.'
      : 'Cancelar a renovação automática? O acesso Premium continua até o fim do período pago.';
    if (!window.confirm(message)) return;

    setCancelLoading(true);
    try {
      const response = await axios.post(`${API}/billing/cancel`);
      setSummary(response.data);
      toast.success('Renovação automática cancelada.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível cancelar a renovação');
    } finally {
      setCancelLoading(false);
    }
  };

  const refreshStatus = async () => {
    setRefreshLoading(true);
    const result = await loadSummary(true);
    if (result?.billing_status === 'authorized' || result?.plan === 'Premium' || result?.plan === 'Trial') {
      toast.success('Plano atualizado.');
    } else {
      toast.info('Ainda aguardando a confirmação do Mercado Pago.');
    }
    setRefreshLoading(false);
  };

  if (loading || !summary) {
    return (
      <div className="flex min-h-64 items-center justify-center text-pink-700">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Carregando plano...
      </div>
    );
  }

  const isCanceled = ['canceled', 'cancelled'].includes((summary.billing_status || '').toLowerCase());
  const canCancel = summary.has_subscription && !summary.cancel_at_period_end && !isCanceled;
  const hasPendingCheckout = Boolean(summary.checkout_url);
  const isTrial = summary.plan === 'Trial';
  const isPremium = summary.plan === 'Premium';
  const isFree = summary.plan === 'Free';

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="billing-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Plano e cobrança</h1>
        <p className="mt-1 text-gray-600">Gerencie seu plano e os limites da sua empresa.</p>
      </div>

      <Card className="border-pink-100 shadow-sm">
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-2xl">
              {isPremium ? <Crown className="h-6 w-6 text-amber-500" /> : null}
              Plano {summary.plan}
            </CardTitle>
            <CardDescription className="mt-2">
              {isTrial && summary.trial_end
                ? `Seu trial termina em ${formatDate(summary.trial_end)}${summary.trial_days_remaining !== null ? ` (${summary.trial_days_remaining} dias restantes)` : ''}.`
                : isPremium && summary.current_period_end
                  ? `Próxima renovação: ${formatDate(summary.current_period_end)}.`
                  : 'Você pode usar o plano Free sem cadastrar pagamento.'}
            </CardDescription>
          </div>
          <span className={`rounded-full px-3 py-1 text-sm font-semibold ${isPremium ? 'bg-amber-100 text-amber-800' : isTrial ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`}>
            {isPremium ? 'Premium' : isTrial ? 'Trial ativo' : 'Free'}
          </span>
        </CardHeader>
        <CardContent className="space-y-4">
          {isCanceled && (summary.trial_end || summary.current_period_end) && (
            <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              Renovação cancelada. Seu acesso permanece até {formatDate(summary.trial_end || summary.current_period_end)}.
            </div>
          )}
          {summary.billing_status === 'pending' && !hasPendingCheckout && (
            <div role="status" className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
              Aguardando confirmação do pagamento pelo Mercado Pago.
            </div>
          )}
          {summary.billing_status === 'creating_checkout' && (
            <div role="status" className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
              Estamos preparando seu checkout. Atualize o status em instantes.
            </div>
          )}
          {isFree && summary.billing_status === 'authorized' && (
            <div role="status" className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
              Assinatura criada. Aguardando a confirmação da primeira cobrança.
            </div>
          )}
          {summary.billing_status === 'paused' && (
            <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              O Mercado Pago pausou a cobrança. O acesso atual tem um período de tolerância de 7 dias.
            </div>
          )}
          {!summary.billing_configured && (
            <div role="status" className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
              A integração de cobrança ainda não foi configurada no servidor. Seu plano Free continua disponível.
            </div>
          )}
          {canCancel && (
            <Button variant="outline" onClick={cancelSubscription} disabled={cancelLoading}>
              {cancelLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Cancelar renovação automática
            </Button>
          )}
          {(isTrial || isPremium) && summary.cancel_at_period_end && (
            <p className="text-sm text-gray-600">A renovação automática está cancelada.</p>
          )}
        </CardContent>
      </Card>

      <Card className="border-pink-100 shadow-sm">
        <CardHeader>
          <CardTitle>Uso do plano</CardTitle>
          <CardDescription>Seus dados continuam disponíveis mesmo quando um limite é atingido.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5 sm:grid-cols-2">
          {Object.entries(RESOURCE_LABELS).map(([resource, label]) => {
            const limit = summary.limits?.[resource];
            const used = summary.usage?.[resource] || 0;
            const percentage = limit ? Math.min(100, (used / limit) * 100) : 0;
            return (
              <div key={resource}>
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-700">{label}</span>
                  <span className="text-gray-500">{used} / {limit ?? 'Ilimitado'}</span>
                </div>
                {limit ? <Progress value={percentage} className="h-2" /> : <div className="h-2 rounded-full bg-emerald-100"><div className="h-2 w-full rounded-full bg-emerald-500" /></div>}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {(isFree || (isCanceled && isTrial)) && !hasPendingCheckout && (
        <Card className="border-pink-200 shadow-sm">
          <CardHeader>
            <CardTitle>{summary.trial_available ? 'Ative seu trial de 90 dias' : 'Assine o Premium'}</CardTitle>
            <CardDescription>
              {summary.trial_available
                ? 'Use todas as funcionalidades por 90 dias. O período escolhido será cobrado automaticamente depois do trial se você não cancelar.'
                : 'Remova os limites e tenha acesso completo ao SweetManager.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {PERIODS.map((period) => (
                <button
                  key={period.id}
                  type="button"
                  onClick={() => setSelectedPeriod(period.id)}
                  className={`rounded-xl border p-4 text-left transition ${selectedPeriod === period.id ? 'border-pink-500 bg-pink-50 ring-2 ring-pink-200' : 'border-gray-200 hover:border-pink-300'}`}
                  data-testid={`billing-cycle-${period.id}`}
                >
                  <span className="flex items-center justify-between font-semibold text-gray-900">
                    {period.label}
                    {selectedPeriod === period.id ? <Check className="h-4 w-4 text-pink-600" /> : null}
                  </span>
                  <span className="mt-2 block text-lg font-bold text-pink-700">{formatCurrency(period.amount)}</span>
                  <span className="mt-1 block text-xs text-gray-500">{period.cadence}</span>
                  <span className="mt-2 block text-xs font-medium text-gray-600">{period.equivalent}</span>
                </button>
              ))}
            </div>

            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-4 text-sm text-gray-700">
              <Checkbox checked={acceptRenewal} onCheckedChange={(checked) => setAcceptRenewal(checked === true)} />
              <span>
                {summary.trial_available
                  ? <>Autorizo o trial gratuito de 90 dias e a cobrança automática de <strong>{formatCurrency(selectedOption.amount)} {selectedOption.cadence}</strong> ao final, com renovação recorrente até eu cancelar. Não haverá cobrança se eu não ativar o trial.</>
                  : <>Autorizo a cobrança inicial de <strong>{formatCurrency(selectedOption.amount)} {selectedOption.cadence}</strong> e as renovações recorrentes até eu cancelar.</>}
                {' '}O pagamento e o meio de cobrança são confirmados no Mercado Pago.
              </span>
            </label>

            <Button
              onClick={() => startCheckout()}
              disabled={!summary.billing_configured || !acceptRenewal || checkoutLoading}
              className="w-full bg-gradient-to-r from-pink-500 to-rose-400 hover:from-pink-600 hover:to-rose-500 sm:w-auto"
              data-testid="billing-checkout-button"
            >
              {checkoutLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Clock3 className="mr-2 h-4 w-4" />}
              {summary.trial_available ? 'Ativar 90 dias grátis' : 'Continuar para assinar'}
            </Button>
          </CardContent>
        </Card>
      )}

      {hasPendingCheckout && (
        <Card className="border-blue-200 shadow-sm">
          <CardHeader>
            <CardTitle>Checkout pendente</CardTitle>
            <CardDescription>
              {summary.billing_cycle && PERIODS.find((period) => period.id === summary.billing_cycle)
                ? `Continue o checkout ${PERIODS.find((period) => period.id === summary.billing_cycle).label.toLowerCase()} para autorizar a assinatura.`
                : 'Continue o checkout para autorizar a assinatura.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 sm:flex-row">
            <Button onClick={() => startCheckout(summary.checkout_url)} className="bg-pink-600 hover:bg-pink-700">
              Continuar no Mercado Pago
            </Button>
            <Button variant="outline" onClick={refreshStatus} disabled={refreshLoading}>
              {refreshLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Atualizar status
            </Button>
          </CardContent>
          <div className="px-6 pb-5 text-sm text-gray-600">
            Seu plano atual continua Free. O trial e a cobrança só começam depois que você concluir e autorizar o checkout no Mercado Pago.
          </div>
        </Card>
      )}
    </div>
  );
};

export default Billing;
